import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/contexts/ToastContext";
import { authApi } from "@/api/auth";
import { getUserAvatarUrl } from "@/utils/avatar";

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function EditProfileModal({ isOpen, onClose }: EditProfileModalProps) {
  const { user, updateUser } = useAuth();
  const { showToast } = useToast();
  const [username, setUsername] = useState("");
  const [isUsernameValid, setIsUsernameValid] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxUsernameLength = 20;
  const minUsernameLength = 4;

  // Pre-fill form with current user data when modal opens
  useEffect(() => {
    if (isOpen && user) {
      setUsername(user.username || "");
      setAvatarPreview(null);
      setError(null);
      setIsUsernameValid(
        (user.username?.length || 0) >= minUsernameLength &&
          (user.username?.length || 0) <= maxUsernameLength
      );
    }
  }, [isOpen, user]);

  if (!isOpen || !user) return null;

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow alphanumeric characters and underscores
    const sanitized = value.replace(/[^a-zA-Z0-9_]/g, "");

    if (sanitized.length <= maxUsernameLength) {
      setUsername(sanitized);
      // Validate username (4-20 characters, no special characters)
      setIsUsernameValid(
        sanitized.length >= minUsernameLength &&
          sanitized.length <= maxUsernameLength
      );
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setError("Only JPEG, PNG, and WebP images are allowed");
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError("Maximum file size is 5MB");
      return;
    }

    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload immediately
    setIsUploadingAvatar(true);
    try {
      const updatedUser = await authApi.uploadAvatar(file);
      updateUser(updatedUser);
    } catch (err: any) {
      console.error("Failed to upload avatar:", err);
      setError(err.response?.data?.message || "Failed to upload avatar");
      setAvatarPreview(null);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    if (!isUsernameValid || isSubmitting) return;

    // Check if username actually changed
    if (username === user.username) {
      onClose();
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const updatedUser = await authApi.updateProfile({ name: username });
      updateUser(updatedUser);
      showToast("Profile updated", "success");
      onClose();
    } catch (err: any) {
      console.error("Failed to update profile:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to save profile. Please try again.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get current avatar URL (preview takes priority, then user's avatar, then fallback)
  const currentAvatarUrl =
    avatarPreview ||
    getUserAvatarUrl(user.avatarUrl, user.walletAddress || user.email, "bottts", 128);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative flex w-full max-w-md flex-col bg-background-light dark:bg-background-dark rounded-3xl p-6 mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={isSubmitting || isUploadingAvatar}
          className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors disabled:opacity-50"
        >
          <span className="material-symbols-outlined text-gray-600 dark:text-gray-400">
            close
          </span>
        </button>

        {/* Header */}
        <div className="flex flex-col items-center gap-2 mb-6">
          <h2 className="text-gray-900 dark:text-white text-2xl font-bold">
            Edit Profile
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm text-center">
            Update your profile picture and username
          </p>
        </div>

        {/* Avatar Section */}
        <div className="flex flex-col items-center gap-4 mb-6">
          <div className="relative">
            <img
              src={currentAvatarUrl}
              alt="Profile avatar"
              className="w-24 h-24 rounded-full border-2 border-primary object-cover bg-gray-200 dark:bg-gray-700"
            />
            <button
              onClick={handleAvatarClick}
              disabled={isUploadingAvatar}
              className="absolute bottom-0 right-0 flex h-8 w-8 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-black dark:border-gray-900 bg-primary text-black hover:scale-110 transition-transform disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Change profile picture"
            >
              {isUploadingAvatar ? (
                <span className="material-symbols-outlined text-lg animate-spin">
                  progress_activity
                </span>
              ) : (
                <span className="material-symbols-outlined text-lg">
                  photo_camera
                </span>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-xs">
            Tap to change photo
          </p>
        </div>

        {/* Username Field */}
        <div className="flex flex-col gap-2 mb-6">
          <div className="flex items-baseline justify-between">
            <label className="text-gray-900 dark:text-white text-sm font-medium">
              Username
            </label>
            <span className="text-gray-500 dark:text-gray-400 text-xs">
              {username.length}/{maxUsernameLength}
            </span>
          </div>
          <div className="relative flex items-center">
            <input
              className="flex h-12 w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 text-base font-normal text-gray-900 dark:text-white placeholder:text-gray-500 focus:border-primary focus:outline-none focus:ring-0 transition-colors"
              placeholder="Enter username"
              value={username}
              onChange={handleUsernameChange}
              maxLength={maxUsernameLength}
            />
            {isUsernameValid && username.length >= minUsernameLength && (
              <span className="material-symbols-outlined absolute right-4 text-lg text-primary">
                check_circle
              </span>
            )}
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-xs">
            {minUsernameLength}-{maxUsernameLength} characters, letters, numbers
            and underscores only
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex gap-2 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 p-3 mb-4">
            <span className="material-symbols-outlined text-red-600 dark:text-red-400 text-lg shrink-0">
              error
            </span>
            <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleSave}
            disabled={!isUsernameValid || isSubmitting || isUploadingAvatar}
            className="flex items-center justify-center h-12 rounded-xl bg-primary text-black font-bold hover:opacity-90 transition-opacity disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-500"
          >
            {isSubmitting ? (
              <>
                <span className="material-symbols-outlined text-lg animate-spin mr-2">
                  progress_activity
                </span>
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
          <button
            onClick={onClose}
            disabled={isSubmitting || isUploadingAvatar}
            className="flex items-center justify-center h-12 rounded-xl bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white font-bold hover:bg-gray-200 dark:hover:bg-white/20 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
